import { Link } from "react-router-dom";
import { Camera, UploadCloud } from "lucide-react";
import Button from "@/components/kit/Button";
import PhoneStage from "@/components/kit/PhoneStage";
import RealScreen from "@/components/kit/RealScreen";
import SectionHeading from "@/components/kit/SectionHeading";
import { FILM_PILL, MANNEQUIN_FILMS, analysisFilm } from "@/data/mannequinFilms";
import { track } from "@/lib/analytics";

/**
 * "Two ways in", right after the home story: the two halves of the product side by side, each on its own phone.
 *   left   Analyse a clip: the Analyse film (a rally is uploaded, the smashes are found, the pose is redrawn with the fix in green, a drill). A muted loop, no voice.
 *   right  Practise live: the squat film (the coach counts every rep aloud and corrects one). Its "Tap for sound" pill is there but it never speaks by itself, the hero's phone does.
 * Both are illustrated examples that draw their own label; the words under them say only what the films show. Words and films live in data/mannequinFilms.js.
 * Two columns at every width (the phones just get smaller on a phone); the floating cards are for wide laptops (xl up), where there is room beside each phone. Each film loads when it is near the screen and plays only while it is on it (RealScreen).
 */
const L = analysisFilm();
const R = MANNEQUIN_FILMS.squat;

// the cards hang off the outer edge of each phone, away from the other one
const cards = (list, side, tops) => list.map(([icon, title, sub], i) => ({
  icon, title, sub, z: [90, 110, 70][i], float: i === 1 ? "b" : "a", mobile: false, className: "max-xl:hidden xl:!max-w-[15rem]",
  position: `${tops[i]} ${side === "left" ? "-left-[13.5rem]" : "-right-[13.5rem]"}`,
}));

function Way({ film, chips, sound, pill, title, line, to, cta, icon, tone, id, onClick }) {
  return (
    <div className="flex flex-col items-center text-center min-w-0" data-testid={`home-way-${id}`}>
      <PhoneStage size="tall" tilt={{ x: 3, y: id === "analyse" ? -8 : 8 }} chips={chips} className="!w-[min(100%,15rem)] lg:!w-[min(100%,17rem)] !pb-4">
        <RealScreen video={film} aspect="9 / 16" fit="cover" sound={sound} autoSound={false} soundClass={pill} />
      </PhoneStage>
      <h3 className="font-heading font-extrabold text-[17px] sm:text-xl lg:text-2xl text-white mt-3">{title}</h3>
      <p className="text-zinc-400 text-[13px] sm:text-[15px] leading-snug mt-1.5 max-w-[19rem]">{line}</p>
      <Button as={Link} to={to} size="md" pill variant={tone} icon={icon} className="mt-3 max-sm:!h-11 max-sm:!px-3 max-sm:!text-[14px] max-sm:w-full max-w-[15rem]" onClick={onClick} data-cta={`home_ways_${id}`}>{cta}</Button>
    </div>
  );
}

export default function TwoWays() {
  return (
    <section data-section="ways" className="relative py-10 md:py-16 bg-zinc-950 overflow-hidden" aria-labelledby="ways-heading" data-testid="home-two-ways">
      <div className="relative container mx-auto px-4 max-w-5xl">
        <SectionHeading id="ways-heading" align="center" eyebrow="Two ways in" title={<>Upload a clip, <span className="text-lime-400">or practise live.</span></>} className="mb-8 md:mb-12 !max-w-[44rem] [text-wrap:balance]" />
        <div className="grid grid-cols-[repeat(2,minmax(0,1fr))] gap-3 sm:gap-8 lg:gap-24 items-start">
          <Way id="analyse" film={L} sound={false} pill={undefined}
            chips={cards(L.chips, "left", ["top-[12%]", "top-[46%]", "top-[78%]"])}
            title="Analyse a clip" line="Upload a rally. It finds your shots, redraws your pose with the fix in green and gives you a drill."
            to="/analyze" cta="Upload a clip" icon={UploadCloud} tone="primary" onClick={() => track("home_ways_click", { door: "analyse" })} />
          <Way id="practise" film={R} sound pill={FILM_PILL}
            chips={cards(R.chips.slice(0, 2), "right", ["top-[34%]", "top-[60%]"])}
            title="Practise live" line="Prop your phone up and move. A voice counts every rep and corrects the one that needs it."
            to="/practice" cta="Try live practice" icon={Camera} tone="secondary" onClick={() => track("home_ways_click", { door: "practise" })} />
        </div>
      </div>
    </section>
  );
}
